import Dialog from '@mui/material/Dialog';
import React from 'react';
import { canConfigureDocumentSigners } from '../../lib/legalDocumentPresentation';
import type {
  AdminDocumentPackageDetail,
  AdminDocumentSignerPlanEntry,
} from '../../legalTypes';
import { AdminButton } from '../components';

const emptySigner = (order: number): AdminDocumentSignerPlanEntry => ({
  organizationName: '',
  personName: '',
  title: '',
  email: '',
  authorityConfirmed: false,
  isRequired: true,
  signingOrder: order,
});

const signerPlanFromDetail = (detail: AdminDocumentPackageDetail): AdminDocumentSignerPlanEntry[] =>
  detail.signers.length > 0
    ? detail.signers.map((signer) => ({
      organizationName: signer.organizationName,
      personName: signer.personName,
      title: signer.title,
      email: signer.email,
      authorityConfirmed: signer.authorityConfirmed,
      isRequired: signer.isRequired,
      signingOrder: signer.signingOrder,
    }))
    : [emptySigner(1)];

const validateSignerPlan = (signers: AdminDocumentSignerPlanEntry[]): string[] => {
  const errors: string[] = [];
  signers.forEach((signer, index) => {
    const label = `Signer ${index + 1}`;
    if (!signer.organizationName.trim() || !signer.personName.trim() || !signer.email.trim()) {
      errors.push(`${label} needs an organization, person, and email.`);
    }
    if (!signer.authorityConfirmed) {
      errors.push(`${label} authority must be confirmed.`);
    }
  });
  if (!signers.some((signer) => signer.isRequired)) {
    errors.push('At least one signer must be required.');
  }
  return errors;
};

interface SignerRowProps {
  signer: AdminDocumentSignerPlanEntry;
  index: number;
  removable: boolean;
  onChange: (index: number, patch: Partial<AdminDocumentSignerPlanEntry>) => void;
  onRemove: (index: number) => void;
}

const SignerRow: React.FC<SignerRowProps> = ({
  signer,
  index,
  removable,
  onChange,
  onRemove,
}) => (
  <fieldset className="admin-legal-fieldset admin-legal-signer-editor">
    <legend>Signer {index + 1}</legend>
    <div className="admin-legal-form-grid">
      <label>Organization<input value={signer.organizationName} onChange={(event) => onChange(index, { organizationName: event.target.value })} /></label>
      <label>Person name<input value={signer.personName} onChange={(event) => onChange(index, { personName: event.target.value })} /></label>
      <label>Title<input value={signer.title} onChange={(event) => onChange(index, { title: event.target.value })} /></label>
      <label>Email<input type="email" value={signer.email} onChange={(event) => onChange(index, { email: event.target.value })} /></label>
      <label>Signing order<input type="number" min={0} max={1000} value={signer.signingOrder} onChange={(event) => onChange(index, { signingOrder: Number(event.target.value) })} /></label>
    </div>
    <label className="admin-legal-check"><input type="checkbox" checked={signer.authorityConfirmed} onChange={(event) => onChange(index, { authorityConfirmed: event.target.checked })} />Authority confirmed</label>
    <label className="admin-legal-check"><input type="checkbox" checked={signer.isRequired} onChange={(event) => onChange(index, { isRequired: event.target.checked })} />Required signer</label>
    {removable && <AdminButton variant="ghost" onClick={() => onRemove(index)}>Remove signer {index + 1}</AdminButton>}
  </fieldset>
);

interface SignerPlanEditorProps {
  detail: AdminDocumentPackageDetail;
  busy: boolean;
  onSave: (signers: AdminDocumentSignerPlanEntry[]) => Promise<AdminDocumentPackageDetail>;
}

const SignerPlanEditor: React.FC<SignerPlanEditorProps> = ({ detail, busy, onSave }) => {
  const [open, setOpen] = React.useState(false);
  const [signers, setSigners] = React.useState(() => signerPlanFromDetail(detail));
  const [errors, setErrors] = React.useState<string[]>([]);
  React.useEffect(() => setSigners(signerPlanFromDetail(detail)), [detail]);
  const canConfigure = canConfigureDocumentSigners(detail);
  const updateSigner = (index: number, patch: Partial<AdminDocumentSignerPlanEntry>) => {
    setSigners((current) => current.map((signer, signerIndex) => signerIndex === index
      ? { ...signer, ...patch }
      : signer));
  };
  const save = async () => {
    const validation = validateSignerPlan(signers);
    setErrors(validation);
    if (validation.length > 0) {
      return;
    }
    try {
      await onSave(signers);
      setOpen(false);
    } catch {
      return;
    }
  };
  return (
    <>
      <AdminButton disabled={busy || !canConfigure} onClick={() => setOpen(true)}>Configure recipient plan</AdminButton>
      {!canConfigure && <p className="admin-legal-disabled-reason">Recipient plan changes are available only after package approval and freeze when delivery starts.</p>}
      <Dialog open={open} aria-labelledby="recipient-plan-editor-title" maxWidth="md" fullWidth className="pipeex-admin-portal admin-legal-dialog" onClose={() => !busy && setOpen(false)}>
        <form className="admin-legal-dialog__body" onSubmit={(event) => { event.preventDefault(); void save(); }}>
          <header><span className="admin-legal-eyebrow">Atomic replacement · current package ETag</span><h2 id="recipient-plan-editor-title">Configure recipient plan</h2><p>Recipient identities and order freeze when delivery starts.</p></header>
          {errors.length > 0 && <div role="alert" aria-label="Recipient plan validation" className="admin-legal-callout admin-legal-callout--danger"><ul>{errors.map((error) => <li key={error}>{error}</li>)}</ul></div>}
          {signers.map((signer, index) => <SignerRow key={index} signer={signer} index={index} removable={signers.length > 1} onChange={updateSigner} onRemove={(removeIndex) => setSigners((current) => current.filter((_, itemIndex) => itemIndex !== removeIndex))} />)}
          <AdminButton onClick={() => setSigners((current) => [...current, emptySigner(current.length + 1)])}>Add signer</AdminButton>
          <footer className="admin-legal-dialog__actions"><AdminButton variant="ghost" disabled={busy} onClick={() => setOpen(false)}>Cancel recipient plan</AdminButton><AdminButton type="submit" variant="primary" disabled={busy}>Save recipient plan</AdminButton></footer>
        </form>
      </Dialog>
    </>
  );
};

export default SignerPlanEditor;
